import { SearchOutlined } from '@ant-design/icons';
import { Button, Input } from 'antd';
import React, { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router';

import { GLOBAL_SEARCH_KEY } from '../../../side-nav/sidenav-constants';
import { useSideNav } from '../../../side-nav/sidenav-hooks';
import { SearchOuterTabKeys, SearchProviderNames } from '../models/global-search';
import { useGlobalSearch } from '../use-global-search';

export interface GlobalSearchViewDelegate {
  onGlobalSearch(): Promise<void>;

  onSearchTermChange(term: string): void;

  clearSearchResults(): void;

  onSelectTab(tabId: SearchProviderNames): void;

  onSelectOuterTab(tabId: SearchOuterTabKeys): void;
}

export function GlobalSearchInputs() {
  const location = useLocation();
  const navigate = useNavigate();

  const { sideNavModel } = useSideNav();
  const { globalSearchModel: model, globalSearchDelegate: delegate } = useGlobalSearch();

  const navigateToGlobalSearch = useCallback(() => {
    if (location.pathname !== GLOBAL_SEARCH_KEY) {
      void navigate(GLOBAL_SEARCH_KEY);
    }
  }, [location.pathname, navigate]);

  const search = useCallback(() => {
    navigateToGlobalSearch();
    void delegate.onGlobalSearch();
  }, [delegate, navigateToGlobalSearch]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      delegate.onSearchTermChange(e.target.value);
    },
    [delegate]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key !== 'Enter') {
        return;
      }
      void search();
    },
    [search]
  );

  const handleSubmit = useCallback(() => {
    void search();
  }, [search]);

  const disabled = model.status === 'loading';
  const globalSearchInputs = sideNavModel.collapsed ? (
    <Button onClick={search} icon={<SearchOutlined />} aria-label="Toggle search" />
  ) : (
    <Input
      maxLength={40}
      value={model.inputValue}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      placeholder={sideNavModel.collapsed ? '' : 'Search...'}
      aria-label="Search in the website"
      id="globalSearch"
      disabled={disabled}
      suffix={<SearchOutlined onClick={handleSubmit} />}
    />
  );

  return <>{globalSearchInputs}</>;
}
